import { Suspense, useMemo, useCallback } from 'react';
import { useThree } from '@react-three/fiber';
import { useCitiesStore } from '@/hooks/useCitiesStore';
import { useShittyMapStore } from '@/hooks/useShittyMapStore';
import { useShittyMapCamera } from '@/hooks/useShittyMapCamera';
import { calculateCityPositions } from '@/utils/shittyMapLayout';
import ShittyMapCity from './ShittyMapCity';

function BackgroundPlane({ onClick }) {
  return (
    <mesh position={[0, 0, -10]} onClick={onClick}>
      <planeGeometry args={[10000, 10000]} />
      <meshBasicMaterial transparent opacity={0} />
    </mesh>
  );
}


function CitySprite({ city, position, isSelected, isHighlighted, isVisible, onClick, onHover, cameraZoom }) {
  return (
    <Suspense fallback={null}>
      <ShittyMapCity
        city={city}
        position={position}
        isSelected={isSelected}
        isHighlighted={isHighlighted}
        isVisible={isVisible}
        onClick={onClick}
        onHover={onHover}
        cameraZoom={cameraZoom}
      />
    </Suspense>
  );
}

export default function ShittyMapWorld() {
  const { allCities } = useCitiesStore();
  const {
    selectedCity,
    setSelectedCity,
    setHoveredCity,
    highlightedCities,
    setDetailPanelOpen,
    cameraPosition,
  } = useShittyMapStore();
  const { focusOnCity } = useShittyMapCamera();
  const { size } = useThree();

  const { positions } = useMemo(() => {
    return calculateCityPositions(allCities);
  }, [allCities]);

  const visibleCityIndices = useMemo(() => {
    if (!positions.length || !size.width) return new Set();

    const zoom = cameraPosition?.zoom || 0.8;
    const camX = cameraPosition?.x || 0;
    const camY = cameraPosition?.y || 0;

    const halfWidth = (size.width / 2) / zoom;
    const halfHeight = (size.height / 2) / zoom;
    const padding = 200;

    const visible = new Set();
    positions.forEach((pos) => {
      if (
        Math.abs(pos.x - camX) < halfWidth + padding &&
        Math.abs(pos.y - camY) < halfHeight + padding
      ) {
        visible.add(pos.cityIndex);
      }
    });

    return visible;
  }, [positions, cameraPosition, size.width, size.height]);

  const positionMap = useMemo(() => {
    return new Map(positions.map((p) => [p.cityIndex, p]));
  }, [positions]);

  const cityMap = useMemo(() => {
    return new Map(allCities.map((c) => [c.index, c]));
  }, [allCities]);

  const handleCityClick = useCallback(
    (city) => {
      setSelectedCity(city);
      setDetailPanelOpen(true);

      const pos = positionMap.get(city.index);
      if (pos) {
        focusOnCity(pos.x, pos.y);
      }
    },
    [setSelectedCity, setDetailPanelOpen, focusOnCity, positionMap]
  );

  const handleCityHover = useCallback(
    (city, isHovered) => {
      setHoveredCity(isHovered ? city : null);
    },
    [setHoveredCity]
  );

  const handleBackgroundClick = useCallback(() => {
    setSelectedCity(null);
    setDetailPanelOpen(false);
  }, [setSelectedCity, setDetailPanelOpen]);

  const hasFilters = highlightedCities.length > 0 && highlightedCities.length < allCities.length;
  const cameraZoom = cameraPosition?.zoom || 0.5;

  return (
    <>
      <BackgroundPlane onClick={handleBackgroundClick} />

      {positions.map((pos) => {
        const city = cityMap.get(pos.cityIndex);
        if (!city) return null;

        const isHighlighted = !hasFilters || highlightedCities.includes(city.index);

        return (
          <CitySprite
            key={pos.cityIndex}
            city={city}
            position={[pos.x, pos.y, pos.z]}
            isSelected={selectedCity?.index === city.index}
            isHighlighted={isHighlighted}
            isVisible={visibleCityIndices.has(city.index)}
            onClick={handleCityClick}
            onHover={handleCityHover}
            cameraZoom={cameraZoom}
          />
        );
      })}
    </>
  );
}
